import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Container } from '@mui/material';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/AuthService';
import { AUTH_BFF_ENABLED } from '@/config/authBff';
import { provisionCurrentUser } from '@/utils/provisionUser';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@equip/utils/logger';
import { useAuthStore, AuthorizationError } from '@/stores/useAuthStore';
import type { AuthResult } from '@/types/Auth';

const log = logger.child({ component: 'AuthCallback' });

// Session storage key used purely to SIGNAL "a token exchange is in progress"
// to useAuthStore.initializeAuth (so it keeps the loading state during a
// refresh instead of flashing the login UI). It is NOT the deduplication
// mechanism — that is the in-memory codeExchanges map below.
const TOKEN_EXCHANGE_KEY = 'token_exchange_in_progress';

// In-memory single-flight for authorization-code exchange.
//
// The callback effect can run more than once for the same authorization code
// (React StrictMode double-mounts in dev, or a genuine component remount). An
// OAuth authorization code is single-use: sending it to the IdP twice yields
// `invalid_code`, which surfaces to the user as "token exchange failed" — even
// when the FIRST exchange actually succeeded. (This also happens for users with
// no eQuip role: the first exchange succeeds but the role gate rejects it, so
// isAuthenticated never flips.) Keying the in-flight — and settled — exchange
// promise by code guarantees the code is exchanged exactly once; every other
// caller awaits the same result. The settled promise is retained for the
// lifetime of the page so a late remount reuses the outcome instead of
// re-sending the consumed code.
const codeExchanges = new Map<string, Promise<AuthResult>>();

export const AuthCallbackPage: React.FC = () => {
  const { setTokens, isAuthenticated, clearAuth } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [initialIntendedRoute] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('intended_route');
  });

  useEffect(() => {
    let cancelled = false;
    const clearAuthFlowArtifacts = () => {
      localStorage.removeItem('silent_login');
      localStorage.removeItem('login_initiated');
      localStorage.removeItem('oauth_state');
      localStorage.removeItem('pkce_verifier');
      sessionStorage.removeItem('auth_init_timestamp');
      sessionStorage.removeItem('auth_in_progress');
      sessionStorage.removeItem(TOKEN_EXCHANGE_KEY);
    };

    const handleCallback = async () => {
      // When loaded inside a hidden iframe (silent re-auth), relay the URL
      // parameters back to the parent window and do nothing else.  This keeps
      // the iframe inert — no sessionStorage writes, no navigation, no token
      // exchange — so an in-progress interactive login is never disturbed.
      let isIframe = false;
      try {
        isIframe = window !== window.top;
      } catch {
        isIframe = true;
      }
      if (isIframe) {
        const searchParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const relaySource =
          searchParams.get('code') || searchParams.get('error') || searchParams.get('state')
            ? searchParams
            : hashParams;
        const relayCode = relaySource.get('code');
        const relayState = relaySource.get('state');
        const relayError = relaySource.get('error');
        if (relayCode && relayState) {
          window.parent.postMessage(
            { type: 'silent-auth-code', code: relayCode, state: relayState },
            window.location.origin,
          );
        } else {
          window.parent.postMessage(
            {
              type: 'silent-auth-error',
              error: relayError ?? (relayCode ? 'missing_state' : 'no_code'),
            },
            window.location.origin,
          );
        }
        return;
      }

      // Parse params from query string first (response_mode=query, our default).
      // Fall back to URL fragment (#code=xxx) in case the IdP uses fragment mode
      // for a particular tenant/app config (e.g. omitted response_mode).
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const urlParams =
        searchParams.get('code') || searchParams.get('error') ? searchParams : hashParams;
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      const isSilentLoginAttempt = localStorage.getItem('silent_login') === 'true';

      // Workforce BFF: the browser landed on the already-registered /auth/callback;
      // forward code+state to the BFF for the server-side exchange (tokens stay in
      // httpOnly cookies) rather than exchanging in the browser.
      if (AUTH_BFF_ENABLED) {
        if (error || !code || !state) {
          if (cancelled) return;
          navigate(`/login?error=${encodeURIComponent(error ?? 'no_code')}`, { replace: true });
          return;
        }
        const bffTokens = await authService.exchangeBffCallback(code, state);
        if (cancelled) return;
        if (!bffTokens) {
          navigate('/login?error=bff_exchange', { replace: true });
          return;
        }
        await setTokens(bffTokens);
        const intended = localStorage.getItem('intended_route');
        localStorage.removeItem('intended_route');
        navigate(intended && intended !== '/login' ? intended : '/', { replace: true });
        return;
      }

      // Mark that an exchange is in progress (signal for initializeAuth); the
      // codeExchanges map below is what actually guarantees single-use of the
      // authorization code.
      sessionStorage.setItem(TOKEN_EXCHANGE_KEY, '1');

      try {
        // Clear one-shot silent-login marker now that callback processing has started.
        localStorage.removeItem('silent_login');
        sessionStorage.removeItem('auth_in_progress');

        if (error) {
          // Silent login should fail quietly to avoid flashing auth error banners
          // during refresh when the app falls back to interactive sign-in.
          if (isSilentLoginAttempt) {
            log.debug('Silent login unavailable', { error, errorDescription });
            clearAuthFlowArtifacts();
            // Another callback may have already succeeded; do not clobber an authenticated session.
            if (useAuthStore.getState().isAuthenticated) return;
            localStorage.setItem('silent_sso_last_fail', Date.now().toString());
            clearAuth();
            if (cancelled) return;
            navigate('/login', { replace: true });
            return;
          }

          log.error('OAuth error', { error, errorDescription });
          clearAuthFlowArtifacts();
          if (cancelled) return;
          clearAuth();
          const loginErrorParams = new URLSearchParams({ error });
          if (errorDescription) loginErrorParams.set('error_description', errorDescription);
          navigate('/login?' + loginErrorParams.toString(), { replace: true });
          return;
        }

        if (!code) {
          if (isSilentLoginAttempt) {
            log.debug('Silent login callback missing authorization code');
            clearAuthFlowArtifacts();
            // Another callback may have already succeeded; do not clobber an authenticated session.
            if (useAuthStore.getState().isAuthenticated) return;
            localStorage.setItem('silent_sso_last_fail', Date.now().toString());
            clearAuth();
            if (cancelled) return;
            navigate('/login', { replace: true });
            return;
          }
          // Guard: if this is a StrictMode second invocation and the first run
          // already succeeded (auth is set, URL code is gone), do not clobber.
          if (useAuthStore.getState().isAuthenticated) return;
          log.error('No authorization code received');
          clearAuthFlowArtifacts();
          if (cancelled) return;
          clearAuth();
          navigate('/login?error=no_code', { replace: true });
          return;
        }

        // Exchange code for tokens. Single-flight by code so a StrictMode
        // double-mount or remount reuses the same settled promise instead of
        // re-sending the single-use authorization code.
        let exchange = codeExchanges.get(code);
        if (!exchange) {
          exchange = (async () => {
            const tokens = await authService.exchangeCodeForToken(code, state);
            await setTokens(tokens);
            return tokens;
          })();
          codeExchanges.set(code, exchange);
        }
        await exchange;

        // Ensure the user is provisioned in the DB. Fire-and-forget — do not
        // block navigation on this; a failure here is non-fatal.
        provisionCurrentUser()
          .then(() => queryClient.invalidateQueries({ queryKey: ['my-person-entity-id'] }))
          .catch((err: unknown) => {
            log.warn('User provisioning request failed', { error: String(err) });
          });

        // Clear any lingering auth session artifacts
        localStorage.removeItem('used_codes');

        log.info('Authentication successful');

        // Invalidate and refetch protected queries after login
        queryClient.invalidateQueries();
        if (initialIntendedRoute) {
          localStorage.removeItem('intended_route');
          if (cancelled) return;
          navigate(initialIntendedRoute, { replace: true });
        } else {
          if (cancelled) return;
          navigate('/', { replace: true });
        }
      } catch (error) {
        if (useAuthStore.getState().isAuthenticated) {
          // Another callback handler may have already completed authentication.
          // Ignore late failures to avoid flashing transient login errors.
          log.debug('Ignoring callback error after authentication already succeeded', {
            error: String(error),
          });
          clearAuthFlowArtifacts();
          if (cancelled) return;
          navigate('/', { replace: true });
          return;
        }

        if (isSilentLoginAttempt) {
          log.debug('Silent login token exchange failed', { error: String(error) });
          clearAuthFlowArtifacts();
          localStorage.setItem('silent_sso_last_fail', Date.now().toString());
          clearAuth();
          if (cancelled) return;
          navigate('/login', { replace: true });
          return;
        }

        // Authorization rejection: the token was valid but the identity has no
        // eQuip application role. The session was already cleared at the gate;
        // surface an explicit "access denied" rather than a generic token error,
        // and never retry (the code is already consumed).
        if (error instanceof AuthorizationError) {
          log.warn('Login rejected: identity not authorized for eQuip');
          clearAuthFlowArtifacts();
          clearAuth();
          if (cancelled) return;
          navigate('/login?error=unauthorized', { replace: true });
          return;
        }

        log.error('Token exchange failed', { error: String(error) });
        clearAuthFlowArtifacts();
        if (cancelled) return;
        clearAuth();
        const failParams = new URLSearchParams({ error: 'token_exchange_failed' });
        if (import.meta.env.DEV && error instanceof Error) {
          failParams.set('error_description', error.message);
        }
        navigate('/login?' + failParams.toString(), { replace: true });
      } finally {
        // Clean up after navigation completes
        setTimeout(() => {
          sessionStorage.removeItem(TOKEN_EXCHANGE_KEY);
        }, 2000);
      }
    };

    handleCallback();
    return () => {
      cancelled = true;
    };
  }, [clearAuth, initialIntendedRoute, navigate, queryClient, setTokens]);

  // Redirect if already authenticated (tokens were set successfully)
  if (isAuthenticated && !initialIntendedRoute) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          Completing sign in...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your credentials
        </Typography>
      </Box>
    </Container>
  );
};
